"use client";

// src/ui/components/calculator/mode-toggle.tsx
//
// Forward/reverse mode switch for the Peptidrechner. "Bereits angemischt"
// (forward — vial/water/dose are known) vs. "Anmisch-Hilfe" (reverse — only
// vial + target dose are known; the calculator finds the water volume).
// A segmented control (white + shadow = active) plus a centered muted sub-line
// that explains the active mode. See design §1.2 (ModeToggle).

import { Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";

interface ModeToggleProps {
	mode: "forward" | "reverse";
	onModeChange: (mode: "forward" | "reverse") => void;
}

export function ModeToggle({ mode, onModeChange }: ModeToggleProps) {
	const t = useTranslations("peptideCalculator");
	const sub = mode === "reverse" ? t("mode.reverseSub") : t("mode.forwardSub");

	const modes: { id: "forward" | "reverse"; label: string; spark?: boolean }[] = [
		{ id: "forward", label: t("mode.forward") },
		{ id: "reverse", label: t("mode.reverse"), spark: true },
	];

	return (
		<div>
			<div
				role="radiogroup"
				aria-label={t("mode.ariaLabel")}
				className="tool-mode-toggle bg-secondary/40 relative grid grid-cols-2 rounded-xl border border-border p-1"
			>
				<span className="tool-mode-pill" data-mode={mode} aria-hidden="true" />
				{modes.map((m) => {
					const active = mode === m.id;
					return (
						<button
							key={m.id}
							type="button"
							role="radio"
							aria-checked={active}
							tabIndex={active ? 0 : -1}
							onClick={() => onModeChange(m.id)}
							onKeyDown={(event) => {
								if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key))
									return;
								event.preventDefault();
								const next =
									event.key === "Home"
										? "forward"
										: event.key === "End"
											? "reverse"
											: mode === "forward"
												? "reverse"
												: "forward";
								onModeChange(next);
								const group = event.currentTarget.parentElement;
								group?.querySelector<HTMLButtonElement>(`button[data-mode="${next}"]`)?.focus();
							}}
							data-mode={m.id}
							className={[
								"relative z-10 flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 sm:px-3",
								active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
							].join(" ")}
						>
							{m.spark && <Sparkles aria-hidden className="h-4 w-4" />}
							<span>{m.label}</span>
						</button>
					);
				})}
			</div>
			<p className="mt-2 text-center text-xs text-muted-foreground">{sub}</p>
		</div>
	);
}
